/**
 * The aperture: a question goes in, grounded records come out.
 *
 * Four steps, none of them clever:
 *   1. embed the query with the pack's own query prefix
 *   2. brute-force cosine top-k over the lattice
 *   3. drop hits below a floor, so a question the pack cannot answer returns
 *      nothing rather than the least-bad thing it happens to contain
 *   4. wrap what survives in the provenance banner
 *
 * Step 3 is the one that earns its keep. Retrieval always returns SOMETHING;
 * top-k has no concept of "nothing relevant here". Without a floor the model
 * is handed irrelevant records and asked to answer from them, which is a
 * machine for producing confident nonsense. The floor is what lets the sentinel
 * ever fire.
 */

import { topK, type Hit } from "../pure/retrieval.ts";
import { normalize } from "../pure/quant.ts";
import { memoryBanner, type SearchResult, type BannerMode } from "../pure/toolParse.ts";
import type { LoadedPack } from "./packStore";
import type { EmbedClient } from "./embedClient";

/**
 * Match a freshly embedded query to the pack's stored dimension.
 *
 * Packs ship Matryoshka-truncated (512 of the embedder's native 768, chosen by
 * measurement) but the embedder still returns its full width, so the query has
 * to be cut the same way the documents were: take the leading dims, THEN
 * renormalize, because a truncated unit vector is no longer unit length.
 *
 * Doing this in one place, keyed off the pack's own manifest, is the point.
 * The first version truncated documents at export and forgot the query path,
 * and the dimension guard below is what caught it rather than a quiet drop in
 * recall.
 */
function fitToPack(vec: Float32Array, dim: number): Float32Array {
  if (vec.length === dim) return vec;
  if (vec.length < dim) {
    throw new Error(
      `embedder returned dim ${vec.length}, which is smaller than the pack's ${dim}. ` +
        `The pack was built with a different embedder.`,
    );
  }
  return normalize(new Float32Array(vec.subarray(0, dim)));
}

/**
 * Minimum cosine for a hit to count as relevant.
 *
 * STILL 0.45, AND NOW KNOWN TO BE WRONG. It shipped as a marked guess owing a
 * precision/recall sweep, and `tools/bench-relevance.mjs` is that sweep. On
 * natural-language questions against spark-kit, with the SAME query style on
 * both sides so the two arms are comparable:
 *
 *   floor   keeps the right fact   admits a question the lattice cannot answer
 *   0.45           100%                          100%      <- this value
 *   0.55            97%                           80%
 *   0.60            90%                           33%
 *   0.62            87%                            3%
 *   0.65            77%                            0%
 *
 * At 0.45 the floor rejects NOTHING. It is not a weak filter, it is an inert
 * one, and every record in Free's gardening-vs-hermes run cleared it including
 * the six about pot-sharing and day length on a question about plant signalling.
 *
 * MOVED TO THE KNEE, 0.62, on Free's call. Two claims of very different
 * strength were being conflated: the evidence that 0.45 is WRONG is
 * overwhelming, since 100%/100% means it rejects nothing at all, while the
 * evidence for 0.62 EXACTLY is n=30 on one lattice whose questions were
 * authored after reading their target facts. Holding 0.45 because 0.62 is
 * imprecise kept the strictly worse of the two.
 *
 * Expect this to move again when task #53 lands question sets for the other
 * four lattices. It is a dial in both the lab and the page's tuning panel, and
 * `tools/bench-relevance.mjs` reproduces the sweep.
 */
export const RELEVANCE_FLOOR = 0.62;

export const DEFAULT_K = 6;

export type ApertureResult = {
  hits: Hit[];
  results: SearchResult[];
  banner: string;
  /** Rows to flare in the constellation. */
  rows: number[];
  embedMs: number;
  searchMs: number;
  /** True when nothing cleared the floor. The caller should let the model say so. */
  empty: boolean;
};

export async function openAperture(
  pack: LoadedPack,
  embedder: EmbedClient,
  query: string,
  k: number = DEFAULT_K,
  /** Forces a banner wording. Left alone it gates on recall strength. */
  bannerMode: BannerMode = "auto",
  /** Override the relevance floor. A dial while the right value is settled. */
  floor?: number,
): Promise<ApertureResult> {
  const t0 = performance.now();
  // The prefix comes from the manifest, never from the app. This is what makes
  // a document/query prefix mismatch structurally impossible.
  const raw768 = await embedder.embedOne(pack.manifest.queryPrefix + query);
  const vec = fitToPack(raw768, pack.vectors.dim);
  const embedMs = performance.now() - t0;

  const t1 = performance.now();
  const raw = topK(pack.vectors, vec, k);
  const searchMs = performance.now() - t1;

  const hits = raw.filter((h) => h.score >= (floor ?? RELEVANCE_FLOOR));
  const results: SearchResult[] = hits.map((h) => {
    const f = pack.facts[h.index];
    return {
      fact: f.fact,
      quote: f.quote ?? undefined,
      ref: f.ref ?? undefined,
      score: h.score,
    };
  });

  return {
    hits,
    results,
    // The persona is not repeated here. The system prompt has just said "You
    // are The Mind's Gardener"; naming them again in the same breath invites a
    // small model to talk about itself in the third person.
    banner: memoryBanner(results, bannerMode),
    rows: hits.map((h) => h.index),
    embedMs,
    searchMs,
    empty: hits.length === 0,
  };
}
